import { ValueTypeEnum } from "~/graphql/supergraphEnums";
import { FormValues, MetadataConfiguration } from "./types";

export const metadataConfigurations: MetadataConfiguration = {
  address_verification: [{ key: "type" }, { key: "date", type: ValueTypeEnum.Date }],
  aml_reliance_letter: [
    { key: "type" },
    { key: "name_of_issuer" },
    { key: "issuer_relationship" },
    { key: "date", type: ValueTypeEnum.Date },
    { key: "meets_requirements" },
  ],
  annual_report: [{ key: "type" }, { key: "description" }],
  authorized_signatory_list: [{ key: "type" }, { key: "description" }],
  confirmation_of_beneficial_owners: [{ key: "description" }],
  confirmation_of_class_beneficiaries: [{ key: "type" }, { key: "description" }],
  evidence_of_formation: [{ key: "type" }, { key: "description" }],
  evidence_of_listing_on_public_exchange: [{ key: "type" }, { key: "description" }],
  evidence_of_regulation: [{ key: "type" }, { key: "description" }],
  form_990: [{ key: "type" }, { key: "description" }],
  formation_document: [{ key: "type" }, { key: "certified", type: ValueTypeEnum.Boolean }],
  id: [
    { key: "type" },
    { key: "certified", type: ValueTypeEnum.Boolean },
    { key: "expiry", type: ValueTypeEnum.Date },
  ],
  kyc_checklist: [
    { key: "as_of_date", type: ValueTypeEnum.Date },
    { key: "collection_status" },
  ],
  organizational_document: [{ key: "type" }, { key: "certified", type: ValueTypeEnum.Boolean }],
  organization_chart: [{ key: "description" }],
  other: [{ key: "description" }],
  payee_mismatch_explanation: [
    { key: "type" },
    { key: "date", type: ValueTypeEnum.Date },
    { key: "description" },
  ],
  register_of_directors: [{ key: "type" }, { key: "description" }],
  register_of_members: [{ key: "type" }, { key: "description" }],
  screening_report: [{ key: "assessment" }, { key: "date", type: ValueTypeEnum.Date }],
  self_certification: [
    { key: "type" },
    { key: "date", type: ValueTypeEnum.Date },
    { key: "description" },
  ],
  share_register: [
    { key: "type" },
    { key: "certified", type: ValueTypeEnum.Boolean },
    { key: "description" },
  ],
  subscription_document: [{ key: "description" }],
  trust_deed: [{ key: "type" }, { key: "description" }],
  waiver: [{ key: "description" }],
  w_series_tax_form: [{ key: "type" }, { key: "date", type: ValueTypeEnum.Date }],
};

export const INITIAL_VALUES: FormValues = {
  relatedProfile: "",
  documentType: undefined,
  isCertifiedDocument: false,
  secondaryDocumentType: "",
  documentStatus: "",
  expiryDate: undefined,
  description: "",
  date: undefined,
  documentExpiryDate: undefined,
  assessment: "",
  collectionStatus: "",
  asOfDate: undefined,
  nameOfIssuer: "",
  issuerRelationship: "",
  meetsRequirements: "",
  entityName: "",
  hasTaxDetails: false,
};

export const DOCUMENT_TYPE_MAPPING = {
  addressVerification: {
    enumValue: "address_verification",
    readableName: "Address Verification",
  },
  amlRelianceLetter: {
    enumValue: "aml_reliance_letter",
    readableName: "AML Reliance Letter",
  },
  annualReport: {
    enumValue: "annual_report",
    readableName: "Annual Report",
  },
  authorizedSignatoryList: {
    enumValue: "authorized_signatory_list",
    readableName: "Authorized Signatory List",
  },
  confirmationOfBO: {
    enumValue: "confirmation_of_beneficial_owners",
    readableName: "Confirmation of Beneficial Owners & Controllers",
    descriptionOnly: {
      required: false,
    },
  },
  confirmationOfNoChange: {
    enumValue: "confirmation_of_no_change",
    readableName: "Confirmation of No Change",
  },
  confirmationOfTheClassOfBeneficiaries: {
    enumValue: "confirmation_of_class_beneficiaries",
    readableName: "Confirmation of the Class of Beneficiaries",
  },
  evidenceOfGov: {
    enumValue: "evidence_of_formation",
    readableName: "Evidence of Government Formation/Control over Entity",
  },
  evidenceOfListingOnPublicExchange: {
    enumValue: "evidence_of_listing_on_public_exchange",
    readableName: "Evidence of Listing on Public Exchange",
  },
  evidenceOfRegulation: {
    enumValue: "evidence_of_regulation",
    readableName: "Evidence of Regulation",
  },
  form990: {
    enumValue: "form_990",
    readableName: "Form 990",
  },
  formation_document: {
    enumValue: "formation_document",
    readableName: "Formation Document",
  },
  id: {
    enumValue: "id",
    readableName: "ID",
  },
  kycChecklist: {
    enumValue: "kyc_checklist",
    readableName: "KYC Checklist",
  },
  organizational: {
    enumValue: "organizational_document",
    readableName: "Organizational Document",
  },
  organizationChart: {
    enumValue: "organization_chart",
    readableName: "Organization Chart",
    descriptionOnly: {
      required: false,
    },
  },
  other: {
    enumValue: "other",
    readableName: "Other",
    descriptionOnly: {
      required: false,
    },
  },
  payeeMismatch: {
    enumValue: "payee_mismatch_explanation",
    readableName: "Payee Mismatch Explanation",
  },
  registerOfDirectors: {
    enumValue: "register_of_directors",
    readableName: "Register of Directors",
  },
  registerOfMembers: {
    enumValue: "register_of_members",
    readableName: "Register of Members",
  },
  screeningResults: {
    enumValue: "screening_report",
    readableName: "Screening Results",
  },
  selfCertification: {
    enumValue: "self_certification",
    readableName: "Self-Certification",
  },
  shareRegister: {
    enumValue: "share_register",
    readableName: "Share Register",
  },
  subscription: {
    enumValue: "subscription_document",
    readableName: "Subscription Document",
    descriptionOnly: {
      required: false,
    },
  },
  trustDeed: {
    enumValue: "trust_deed",
    readableName: "Trust Document",
  },
  waiver: {
    enumValue: "waiver",
    readableName: "Waiver",
    descriptionOnly: {
      required: true,
    },
  },
  wSeriesTaxForm: {
    enumValue: "w_series_tax_form",
    readableName: "W Series Tax Form",
  },
};

export const documentTypesOptions = Object.values(DOCUMENT_TYPE_MAPPING).map(
  ({ enumValue, readableName }) => ({
    value: enumValue,
    label: readableName,
  }),
);

export const getFilteredDocumentTypesOptions = (includeSelfCertification: boolean) => {
  if (includeSelfCertification) {
    return documentTypesOptions;
  }
  return documentTypesOptions.filter(
    (option) => option.value !== DOCUMENT_TYPE_MAPPING.selfCertification.enumValue,
  );
};

export const useDocumentTypesOptions = (isFatcaCrsEnabled: boolean) => {
  return getFilteredDocumentTypesOptions(isFatcaCrsEnabled);
};

export const descriptionOnlyDocumentTypes = {
  [DOCUMENT_TYPE_MAPPING.confirmationOfBO.enumValue]: {
    required: DOCUMENT_TYPE_MAPPING.confirmationOfBO.descriptionOnly.required,
  },
  [DOCUMENT_TYPE_MAPPING.organizationChart.enumValue]: {
    required: DOCUMENT_TYPE_MAPPING.organizationChart.descriptionOnly.required,
  },
  [DOCUMENT_TYPE_MAPPING.other.enumValue]: {
    required: DOCUMENT_TYPE_MAPPING.other.descriptionOnly.required,
  },
  [DOCUMENT_TYPE_MAPPING.subscription.enumValue]: {
    required: DOCUMENT_TYPE_MAPPING.subscription.descriptionOnly.required,
  },
};

export const completedDocumentStatuses = ["verified", "invalid", "maker_checked"];

export const JOINT_PROFILE_ALLOWED_DOCUMENT_TYPES = new Set([
  DOCUMENT_TYPE_MAPPING.other.enumValue,
  DOCUMENT_TYPE_MAPPING.waiver.enumValue,
  DOCUMENT_TYPE_MAPPING.subscription.enumValue,
]);

export const JOINT_PROFILE_RESTRICTED_DOC_TYPE_TOOLTIP =
  "Document type is not accepted for joint ownership account type.";
